import { useCallback, useEffect, useMemo, useState } from 'react'
import type { DirectorHistoryItem, DirectorPendingItem } from '@/types/director'
import { ABSENCE_TYPE_LABELS, DECISION_TYPE_LABELS, PAYMENT_TYPE_LABELS } from '@/types/director'
import {
  avatarColorOf, formatDeadline, groupPendingByDeadline, initialsOf,
} from '@/utils/caseList'
import { deriveWorkflowColumn } from '@/utils/directorWorkflow'
import { Search, ChevronLeft, ChevronRight } from 'lucide-react'

export type CaseListTab = 'pending' | 'history'

interface CaseListPaneProps {
  pending: DirectorPendingItem[]
  history: DirectorHistoryItem[]
  activeTab: CaseListTab
  onTabChange: (tab: CaseListTab) => void
  selectedId: string | null
  onSelect: (id: string) => void
  pendingLoading: boolean
  historyLoading: boolean
}

const STATUS_DOT: Record<string, string> = {
  documents_pending: 'bg-orange-500',
  ready: 'bg-green-500',
}

const STATUS_LABEL: Record<string, string> = {
  documents_pending: 'Dokumente offen',
  ready: 'Bereit',
}

function deadlineBadgeClasses(group: string): string {
  switch (group) {
    case 'overdue': return 'text-red-600 font-semibold'
    case 'today': return 'text-red-600 font-semibold'
    case 'this_week': return 'text-gray-600'
    default: return 'text-gray-400'
  }
}

interface HistoryGroup {
  key: string
  label: string
  items: DirectorHistoryItem[]
}

function groupHistoryByDate(items: DirectorHistoryItem[]): HistoryGroup[] {
  const groups = new Map<string, DirectorHistoryItem[]>()
  for (const h of items) {
    const key = (h.created_at || '').slice(0, 10) || 'unbekannt'
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(h)
  }
  return [...groups.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, items]) => {
      let label = key
      try {
        label = new Date(key).toLocaleDateString('de-CH', {
          weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric',
        })
      } catch { /* Fallback key */ }
      return { key, label, items }
    })
}

/** Fallakte v2 (P5.1): Master-Liste mit Suche, Tabs, Frist-Gruppen, Tastatur-Navigation. */
export function CaseListPane({
  pending, history, activeTab, onTabChange, selectedId, onSelect,
  pendingLoading, historyLoading,
}: CaseListPaneProps) {
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  // Debounce 200 ms (P5.1)
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 200)
    return () => clearTimeout(t)
  }, [searchInput])

  const matchesPending = useCallback((item: DirectorPendingItem): boolean => {
    if (!search) return true
    const haystack = [
      item.requesting_teacher_name,
      item.subject_name ?? '',
      item.klass_name ?? '',
      item.absence_type_label ?? '',
      item.absence_type ? ABSENCE_TYPE_LABELS[item.absence_type] ?? item.absence_type : '',
    ].join(' ').toLowerCase()
    return haystack.includes(search)
  }, [search])

  const filteredPending = useMemo(() => pending.filter(matchesPending), [pending, matchesPending])

  const deadlineGroups = useMemo(
    () => groupPendingByDeadline(filteredPending),
    [filteredPending],
  )

  const filteredHistory = useMemo(() => {
    if (!search) return history
    return history.filter(h =>
      [h.requesting_teacher_name, h.subject_name ?? ''].join(' ').toLowerCase().includes(search))
  }, [history, search])

  const historyGroups = useMemo(() => groupHistoryByDate(filteredHistory), [filteredHistory])

  // Flache Tastatur-Reihenfolge über alle sichtbaren Pending-Ids
  const flatIds = useMemo(
    () => deadlineGroups.flatMap(g => g.items.map(i => i.id)),
    [deadlineGroups],
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (activeTab !== 'pending' || flatIds.length === 0) return
    if (e.key === 'Enter') {
      e.preventDefault()
      const id = selectedId && flatIds.includes(selectedId) ? selectedId : flatIds[0]!
      onSelect(id)
      return
    }
    if (!selectedId || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) return
    e.preventDefault()
    const idx = flatIds.indexOf(selectedId)
    const next = e.key === 'ArrowDown'
      ? Math.min(idx + 1, flatIds.length - 1)
      : Math.max(idx - 1, 0)
    onSelect(flatIds[next]!)
    // Fallakte v3 (B6): Auswahl sichtbar halten (guard für jsdom)
    document
      .querySelector(`[data-case-id="${flatIds[next]}"]`)
      ?.scrollIntoView?.({ block: 'nearest' })
  }

  return (
    <div className="w-full h-full min-h-0 border-r border-gray-200 bg-white flex flex-col overflow-hidden">
      <div className="p-3 border-b border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-base font-bold">Alle Fälle</h1>
          <span className="text-xs text-gray-400">{pending.length} offen</span>
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-gray-400" />
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg"
            placeholder="Lehrer, Fach, Klasse …"
            aria-label="Fälle durchsuchen"
          />
        </div>
        <div className="flex gap-1 mt-2 text-[11px]" role="tablist" aria-label="Fallansichten">
          <button
            type="button"
            role="tab"
            id="case-tab-pending"
            aria-selected={activeTab === 'pending'}
            aria-controls="case-tabpanel"
            onClick={() => onTabChange('pending')}
            className={`px-2.5 py-1 rounded-full font-semibold ${
              activeTab === 'pending' ? 'bg-slate-900 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            Offen ({pending.length})
          </button>
          <button
            type="button"
            role="tab"
            id="case-tab-history"
            aria-selected={activeTab === 'history'}
            aria-controls="case-tabpanel"
            onClick={() => onTabChange('history')}
            className={`px-2.5 py-1 rounded-full font-semibold ${
              activeTab === 'history' ? 'bg-slate-900 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            Verlauf ({history.length})
          </button>
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto overflow-x-hidden focus:outline-none px-2"
        role="tabpanel"
        id="case-tabpanel"
        aria-labelledby={activeTab === 'pending' ? 'case-tab-pending' : 'case-tab-history'}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        data-testid="case-list"
      >
        {activeTab === 'pending' && (
          <>
            {pendingLoading && (
              <p className="px-4 py-6 text-xs text-gray-400 text-center">Fälle werden geladen…</p>
            )}
            {!pendingLoading && filteredPending.length === 0 && (
              <p className="px-4 py-8 text-xs text-gray-400 text-center">
                {search ? 'Keine Treffer für die Suche.' : 'Keine offenen Fälle.'}
              </p>
            )}
            {!pendingLoading && deadlineGroups.map(group => (
              <div key={group.key}>
                <div
                  className={`px-1 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider ${
                    group.key === 'overdue' || group.key === 'today' ? 'text-red-500' : 'text-gray-400'
                  }`}
                >
                  {group.label} ({group.items.length})
                </div>
                {group.items.map(item => {
                  const selected = item.id === selectedId
                  const column = deriveWorkflowColumn(item)
                  const deadline = formatDeadline(item.expires_at)
                  const paused = group.key === 'paused'
                  return (
                    <button
                      key={item.id}
                      type="button"
                      data-case-id={item.id}
                      aria-current={selected ? 'true' : undefined}
                      onClick={() => onSelect(item.id)}
                      className={`w-full box-border text-left mb-2 rounded-lg border p-2.5 transition ${
                        selected
                          ? 'border-blue-600 bg-blue-50/60 ring-2 ring-blue-500'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <span className="flex items-center gap-2.5 min-w-0">
                        <span
                          className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                          style={{ backgroundColor: avatarColorOf(item.requesting_teacher_name) }}
                          aria-hidden
                        >
                          {initialsOf(item.requesting_teacher_name)}
                        </span>
                        <span className="min-w-0 flex-1 overflow-hidden">
                          <span className="block text-sm font-semibold truncate">
                            {item.requesting_teacher_name}
                          </span>
                          <span className="block text-[11px] text-gray-500 truncate">
                            {item.lesson_count} Lektionen
                          </span>
                        </span>
                        {item.absence_type && item.absence_type !== 'other' && (
                          <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-100 text-gray-600 font-medium shrink-0">
                            {item.absence_type_label || ABSENCE_TYPE_LABELS[item.absence_type] || item.absence_type}
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-2 mt-1.5 pl-9 text-[10px] min-w-0">
                        <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[column]}`} aria-hidden />
                        <span className="text-gray-500 shrink-0">{STATUS_LABEL[column]}</span>
                        <span className={`ml-auto truncate ${deadlineBadgeClasses(group.key)}`}>
                          {paused ? '⏸ pausiert' : deadline ?? ''}
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            ))}
          </>
        )}

        {activeTab === 'history' && (
          <>
            {historyLoading && (
              <p className="px-4 py-6 text-xs text-gray-400 text-center">Verlauf wird geladen…</p>
            )}
            {!historyLoading && filteredHistory.length === 0 && (
              <p className="px-4 py-8 text-xs text-gray-400 text-center">
                {search ? 'Keine Treffer für die Suche.' : 'Noch keine Entscheidungen.'}
              </p>
            )}
            {!historyLoading && historyGroups.map(group => (
              <div key={group.key}>
                <div className="px-1 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  {group.label}
                </div>
                {group.items.map(h => {
                  const selected = h.substitution_request_id === selectedId
                  return (
                    <button
                      key={h.decision_id}
                      type="button"
                      data-case-id={h.substitution_request_id ?? h.decision_id}
                      aria-current={selected ? 'true' : undefined}
                      onClick={() => h.substitution_request_id && onSelect(h.substitution_request_id)}
                      className={`w-full box-border text-left mb-2 rounded-lg border p-2.5 transition ${
                        selected
                          ? 'border-blue-600 bg-blue-50/60 ring-2 ring-blue-500'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <span className="flex items-center gap-2.5 min-w-0">
                        <span
                          className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                          style={{ backgroundColor: avatarColorOf(h.requesting_teacher_name) }}
                          aria-hidden
                        >
                          {initialsOf(h.requesting_teacher_name)}
                        </span>
                        <span className="min-w-0 flex-1 overflow-hidden">
                          <span className="block text-sm font-semibold truncate">{h.requesting_teacher_name}</span>
                          <span className="block text-[11px] text-gray-500 truncate">
                            {h.subject_name || '—'} · {h.director_name}
                          </span>
                        </span>
                        <span className="flex flex-col items-end gap-0.5 shrink-0">
                          <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-100 text-gray-700 font-medium">
                            {DECISION_TYPE_LABELS[h.decision_type] || h.decision_type}
                          </span>
                          {h.decision_type === 'approved' && (
                            <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${
                              h.payment_type === 'school'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {PAYMENT_TYPE_LABELS[h.payment_type] || h.payment_type}
                            </span>
                          )}
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            ))}
          </>
        )}
      </div>

      <div className="px-3 py-2 border-t border-gray-100 text-[10px] text-gray-400 flex items-center gap-3">
        <span className="flex items-center gap-1"><ChevronLeft className="w-3 h-3" />↑↓</span>
        <span>Navigieren · Enter öffnet</span>
        <ChevronRight className="w-3 h-3 ml-auto" />
      </div>
    </div>
  )
}
